
import { GoogleGenAI } from "@google/genai";
import { HexagramResult, OracleFacts, MasterInterpretation } from "../types.ts";

const cleanJsonString = (raw: string): string => {
  return raw.replace(/```json\n?|```/g, "").trim();
};

/**
 * 助手函数：调用 DeepSeek API
 */
async function callDeepSeek(apiKey: string, messages: {role: string, content: string}[], isJson: boolean = true) {
  const body: any = {
    model: "deepseek-chat",
    messages: messages,
  };

  if (isJson) {
    body.response_format = { type: 'json_object' };
  }

  const response = await fetch("https://api.deepseek.com/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${apiKey}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({}));
    throw new Error(errData.error?.message || `DeepSeek API 响应异常: ${response.status}`);
  }

  const data = await response.json();
  return data.choices[0].message.content;
}

/**
 * 测试 API 密钥是否有效
 */
export const testConnection = async (provider: 'gemini' | 'deepseek', apiKey: string): Promise<boolean> => {
  if (!apiKey) return false;
  try {
    if (provider === 'gemini') {
      const ai = new GoogleGenAI({ apiKey });
      const res = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: "ok",
        config: { maxOutputTokens: 1 }
      });
      return !!res.text;
    } else {
      const res = await fetch("https://api.deepseek.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [{ role: "user", content: "hi" }],
          max_tokens: 1
        })
      });
      return res.ok;
    }
  } catch (e) {
    console.error(`Connection test failed for ${provider}:`, e);
    return false;
  }
};

/**
 * 获取神谕解读（包含 AI 实时起卦计算）
 */
export const getOracleInterpretation = async (
  question: string,
  nums: { a: number, b: number, c: number },
  dsApiKey: string,
  gmApiKey?: string
): Promise<{ hexagram: HexagramResult; facts: OracleFacts; masterResponse: MasterInterpretation }> => {
  
  const prompt = `你是一位精通《易经》的数字起卦大师“黄慧”。
  请根据用户输入的三个数字 [${nums.a}, ${nums.b}, ${nums.c}] 进行起卦计算，并针对问题“${question}”给出极致深度的解读。

  【起卦逻辑】
  1. 下卦：${nums.a} 除以 8 的余数（若余数为0则取8）。对应：1乾, 2兑, 3离, 4震, 5巽, 6坎, 7艮, 8坤。
  2. 上卦：${nums.b} 除以 8 的余数（若余数为0则取8）。对应关系同上。
  3. 变爻：${nums.c} 除以 6 的余数（若余数为0则取6）。对应卦象从下往上第1-6爻。

  【输出要求】
  必须严格以 JSON 格式返回以下字段，不要在回复中包含任何关于字数限制的指令文字：
  1. "hexagram": {
     "originalHexagramName": "本卦名",
     "transformedHexagramName": "之卦名",
     "changingLineIndex": 变爻位置(1-6),
     "originalLines": [6位爻值],
     "transformedLines": [变化后的6位爻值]
  }
  2. "facts": {
     "hexagramName": "本卦名",
     "hexagramText": "本卦卦辞原文",
     "changingLineText": "变爻爻辞原文",
     "plainInterpretation": "白话含义概括"
  }
  3. "masterResponse": {
     "situation": "请结合本卦的卦象、爻象意象，对用户当前的处境进行极其丰富、细腻的剖析。字数要充实，需体现出事物发展的微妙阶段。",
     "insight": "从易理辩证哲学出发，结合动爻的变化趋势（即从本卦到之卦的转变），揭示此事背后的天机、因果与时位启示。语感要庄重且充满灵性。",
     "advice": "给出最具体、可落地的三条行动指南。必须使用 1. 2. 3. 这种分段编号格式显示。"
  }

  回复语言：中文。
  【字数约束（仅供你内部参考）】：现状分析与易理启示两部分加起来字数需在 500 字左右。行动指南也要详尽。总解读字数约 600-800 字。`;

  try {
    let responseText: string;
    
    if (gmApiKey) {
      const ai = new GoogleGenAI({ apiKey: gmApiKey });
      const res = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: prompt,
        config: { 
          responseMimeType: "application/json"
        }
      });
      responseText = res.text || "{}";
    } else {
      responseText = await callDeepSeek(dsApiKey, [
        { role: "system", content: "你是一位文字功底深厚、易学造诣极高的神谕大师。你擅长用宏大的意象与微观的剖析来解读命运。你的文字充满了美感与指引力。" },
        { role: "user", content: prompt }
      ], true);
    }

    const data = JSON.parse(cleanJsonString(responseText));
    
    const hexagram: HexagramResult = {
      ...data.hexagram,
      upperTrigram: { id: 0, name: '', symbol: '', lines: [], nature: '' },
      lowerTrigram: { id: 0, name: '', symbol: '', lines: [], nature: '' }
    };

    return { 
      hexagram, 
      facts: data.facts, 
      masterResponse: data.masterResponse 
    };
  } catch (error: any) {
    console.error("Oracle Interpretation Error:", error);
    throw new Error(error.message || "通灵感应受阻，请确保 API 密钥有效。");
  }
};

/**
 * 创建对话 Session
 */
export const createOracleChat = (
  initialQuestion: string,
  facts: OracleFacts,
  hexagram: HexagramResult,
  apiKey: string
) => {
  const history: {role: string, content: string}[] = [
    { 
      role: "system", 
      content: `你现在化身为隐居深山的周易神谕者“黄慧”。用户刚才起了一卦并得到了你的初步解读。
      【卦象背景】
      - 问事：${initialQuestion}
      - 本卦：${hexagram.originalHexagramName}（卦辞：${facts.hexagramText}）
      - 动爻：第${hexagram.changingLineIndex}爻（爻辞：${facts.changingLineText}）
      - 趋向：${hexagram.transformedHexagramName}（之卦）

      【对话高级准则】
      1. **定性判断**：针对用户的追问，你必须给出一个**倾向性明显的结论**。如果是问成败，应告知“成算较大”或“目前无望”等方向；如果是问选择，应指出“利在何方”。
      2. **拒绝绝对**：在给出明确方向后，必须补充“变数”条件。你可以说“依卦象看，此事【大有可为】，然需防口舌之争，若能内敛守静，则圆满可期”。严禁说“一定成功”或“必然失败”。
      3. **意象指引**：用卦象的物理意象来辅助回答。
      4. **大师气场**：语言半文半白，辞藻典雅，语调慈悲但果断。
      5. **追问处理**：如果用户的问题偏离卦象，可以提醒他“卦象已现此端，莫要强求他路”。
      6. **字数控制**：每句回复控制在150字以内，不输出JSON。`
    }
  ];

  return {
    sendMessage: async (msg: { message: string }) => {
      history.push({ role: "user", content: msg.message });
      const content = await callDeepSeek(apiKey, history, false);
      history.push({ role: "assistant", content: content });
      return { text: content };
    }
  };
};
